/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react';
import CSVReader from 'react-csv-reader';
import { Button, Typography } from '@material-ui/core';
import { css } from '@emotion/core';
import { useHistory, Link } from 'react-router-dom';
import { toJS } from 'mobx';
import { Parser } from 'json2csv';
import { CSVLink } from 'react-csv';
import DownloadIcon from '@material-ui/icons/GetApp';
import { WideBox, FlexDiv, ButtonFlex } from '../styles/styles';
import { DealSchemaCSV, DealTransactionSchemaCSV } from '../../validation';
import { useRootStore } from '../../stores/BridgeProvider';
import { COLORS } from '../util/Colors';
import Spinner from '../util/Spinner';

const uploadContainer = css`
  display: flex;
  justify-content: space-around;
  .csv-reader-input {
    border: 1px solid #bdbdbd;
    border-radius: 5px;
    padding: 8px;
    margin-top: 1rem;
  }
  .downloadFile {
    color: ${COLORS.blue};
    font-size: 0.8rem;
    display: block;
    margin-top: 0.6rem;
  }
`;

const spacer = css`
  margin-left: 1.2rem;
`;

const headerRequirements = css`
  margin-left: 1.2rem;
  b {
    width: 150px;
    display: inline-block;
  }
`;

const downloadErrors = css`
  margin: 0 auto;
  margin-top: 0.8rem;
  a {
    color: ${COLORS.red};
    font-size: 0.9rem;
    display: block;
    align-items: center;
    display: flex;
    width: auto;
  }
`;

const papaparseOptions = {
  header: true,
  dynamicTyping: true,
  skipEmptyLines: true,
  transformHeader: header => header.replace(/\W/g, '_'),
};

const formatForPersist = (deals, dealTransactions) => {
  deals.forEach(d => {
    if (
      !Object.prototype.hasOwnProperty.call(d, 'DealNumber') ||
      d.DealNumber === undefined ||
      d.DealNumber === null ||
      d.DealNumber === ''
    ) {
      d.DealNumber = 'AUTO-GENERATE';
    }
  });
  // clone objects to prevent mutation
  const formattedDealMap = JSON.parse(
    JSON.stringify({
      Deals: deals,
      DealTransactions: dealTransactions,
      // dtvts: [],
      // voucherTransactions: [],
      // vouchers: [],
    }),
  );
  const removeList = [
    'ApprovedByName',
    'ModifiedTimestamp',
    'ModifiedBy',
    'CreatedTimestamp',
    'CreatedBy',
    'IsEditable',
  ];
  Object.keys(formattedDealMap).forEach(key => {
    formattedDealMap[key].forEach(arrayObj => {
      const curArrayObj = arrayObj;
      Object.keys(arrayObj).forEach(arrayObjKey => {
        // Remove all null keys
        if (curArrayObj[arrayObjKey] == null) {
          delete curArrayObj[arrayObjKey];
        }
        // TODO: UPDATE THIS
        // Remove fields that Bosman has defined and "Pending" key
        else if (removeList.includes(arrayObjKey))
          delete curArrayObj[arrayObjKey];
        // Anything with tmp- --> prefix with Pending
        else if (
          typeof curArrayObj[arrayObjKey] === 'string' &&
          curArrayObj[arrayObjKey].includes('tmp-')
        ) {
          curArrayObj[`Pending${arrayObjKey}`] = curArrayObj[arrayObjKey];
          delete curArrayObj[arrayObjKey];
        }
      });
    });
  });

  return formattedDealMap;
};

const CsvImport = () => {
  const { authStore, dataStore } = useRootStore();
  const [isLoading, setLoading] = useState(true);
  const history = useHistory();
  const [deals, setDeals] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [errorsFile, setErrorsFile] = useState(null);
  const { transactionCategories, accounts } = dataStore;
  const parser = new Parser();

  useEffect(() => {
    const initialize = async () => {
      const requests = [];
      if (transactionCategories.length === 0) {
        requests.push(dataStore.retrieveTransactionCategories());
      }
      if (accounts.length === 0) {
        requests.push(dataStore.retrieveAccounts());
      }
      await Promise.all(requests);
      setLoading(false);
    };
    initialize();
  }, []);
  if (isLoading) return <Spinner />;

  const validateDeals = async () => {
    const result = await Promise.all(
      deals.map(async deal => DealSchemaCSV.isValid(deal)),
    );
    console.log('DEAL results', result);
    const errorIndex = result.indexOf(false);
    if (errorIndex > -1) {
      window.alert(`Validation failed for Deal at index ${errorIndex}`);
      return false;
    }
    return true;
  };

  const validateTransactions = async () => {
    const result = await Promise.all(
      transactions.map(async transaction =>
        DealTransactionSchemaCSV.isValid(transaction),
      ),
    );
    console.log('DEAL TRANSACTION results', result);
    const errorIndex = result.indexOf(false);
    if (errorIndex > -1) {
      window.alert(
        `Validation failed for Deal Transaction at index ${errorIndex}`,
      );
      return false;
    }
    return true;
  };

  const validateTransactionObjectCodesExist = () => {
    transactions.forEach(transaction => {
      const {
        TranCategoryCode,
        DebitAccountCode,
        CreditAccountCode,
      } = transaction;

      if (
        transactionCategories.findIndex(
          ({ Code }) => Code === TranCategoryCode,
        ) === -1
      ) {
        // TranCategoryCode doesn't exist so fail it;
        transaction.Invalid = true;
        transaction.TranCategoryError = 'DOES NOT EXIST';
      }

      if (
        accounts.findIndex(({ AccountCode }) => {
          return parseInt(AccountCode, 10) === parseInt(DebitAccountCode, 10);
        }) === -1
      ) {
        // DebitAccountCode doesn't exist so fail it;
        transaction.Invalid = true;
        transaction.DebitAccountError = 'DOES NOT EXIST';
      }

      if (
        accounts.findIndex(
          ({ AccountCode }) =>
            parseInt(AccountCode, 10) === parseInt(CreditAccountCode, 10),
        ) === -1
      ) {
        // CreditAccountCode doesn't exist so fail it;
        transaction.Invalid = true;
        transaction.CreditAccountError = 'DOES NOT EXIST';
      }
    });

    if (transactions.some(r => r.Invalid)) {
      const csvOutput = parser.parse(transactions);
      window.alert(
        `Some Account and Transaction Category Codes do not exist. See error file for details`,
      );
      setErrorsFile(csvOutput);
      return false;
    }

    return true;
  };

  const createDeals = async () => {
    setErrorsFile(null);
    const validDeals = await validateDeals();
    if (!validDeals) return;
    const validTransactions = await validateTransactions();
    if (!validTransactions) return;
    const validTranObjects = validateTransactionObjectCodesExist();
    if (!validTranObjects) return;
    try {
      const values = formatForPersist(deals, transactions);
      await authStore.request.post(
        `${process.env.REACT_APP_API}/api/create-deals-from-csv`,
        {
          values,
        },
      );
      history.push('/deals');
    } catch (e) {
      console.error(e);
      window.alert(`Error creating deals`);
    }
  };

  return (
    <FlexDiv>
      <WideBox style={{ overflowX: 'auto', minHeight: '0px' }}>
        <Typography variant="h4" gutterBottom style={{ marginBottom: '2rem' }}>
          Import your deals and transactions from CSV files
        </Typography>
        <div css={uploadContainer}>
          <div>
            <Typography variant="h4" gutterBottom>
              Upload Deals
            </Typography>
            <CSVReader
              onFileLoaded={data => setDeals(data)}
              parserOptions={papaparseOptions}
            />
            <Link
              className="downloadFile"
              to="/files/deals.csv"
              target="_blank"
              download
            >
              Download Deals Template
            </Link>
          </div>
          <div>
            <Typography variant="h4" gutterBottom>
              Upload Transactions
            </Typography>
            <CSVReader
              onFileLoaded={data => setTransactions(data)}
              parserOptions={papaparseOptions}
            />
            <Link
              className="downloadFile"
              to="/files/dealTransactions.csv"
              target="_blank"
              download
            >
              Download Deal Transactions Template
            </Link>
          </div>
        </div>

        <ButtonFlex style={{ justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={createDeals}
            disabled={deals.length === 0}
          >
            Publish deals
          </Button>
        </ButtonFlex>
        {errorsFile && (
          <div css={downloadErrors}>
            <CSVLink
              filename="transactionErrors.csv"
              data={errorsFile}
              target="_blank"
            >
              Download Errors
              <DownloadIcon />
            </CSVLink>
          </div>
        )}
      </WideBox>
      <WideBox style={{ textAlign: 'left', minHeight: 'auto' }}>
        <Typography
          variant="h4"
          style={{
            textAlign: 'center',
          }}
        >
          Header Requirements:
        </Typography>
        <Typography
          variant="subtitle1"
          style={{
            textAlign: 'center',
            marginBottom: '2rem',
          }}
        >
          * marks required header
        </Typography>
        <Typography variant="subtitle1">
          <b>Important:</b> Dates must be formatted as yyyy-mm-dd
        </Typography>
        <br />
        <Typography variant="subtitle1">
          <b>deals.csv:</b>
        </Typography>
        <div css={headerRequirements}>
          <div css={spacer}>
            <Typography variant="subtitle1">
              <b>Title:</b> * Deal title
            </Typography>
            <Typography variant="subtitle1">
              <b>DealNumber:</b> Deal number - if blank, it will be auto
              assigned
            </Typography>
            <Typography variant="subtitle1">
              <b>AgentContactName:</b> Name of the agent associated with the
              deal
            </Typography>
            <Typography variant="subtitle1">
              <b>Description:</b> Deal description
            </Typography>
            <Typography variant="subtitle1">
              <b>ContractDate:</b> * Deal contract date
            </Typography>
            <Typography variant="subtitle1">
              <b>TransactionDate:</b> * Deal transaction date
            </Typography>
            <Typography variant="subtitle1">
              <b>BillableDate:</b> * Deal billable date
            </Typography>
            <Typography variant="subtitle1">
              <b>PayableDate:</b> * Deal payable date
            </Typography>
            <Typography variant="subtitle1">
              <b>StatusName:</b> * Deal status - one of: Draft, Submitted,
              Approved, Closed, Void, Deleted
            </Typography>
          </div>
        </div>
        <br />
        <Typography variant="subtitle1">
          <b>dealTransactions.csv:</b>
        </Typography>
        <div css={headerRequirements}>
          <div css={spacer}>
            <Typography variant="subtitle1">
              <b>DealTitle:</b> * Title of deal associated with Transaction
            </Typography>
            <Typography variant="subtitle1">
              <b>TranType:</b> Transaction Type - one of: AR, AP, JE
            </Typography>
            <Typography variant="subtitle1">
              <b>TranCategoryCode:</b> * Transaction Category code
            </Typography>
            <Typography variant="subtitle1">
              <b>ContactName:</b> * Transaction Contact Name
            </Typography>
            <Typography variant="subtitle1">
              <b>Price:</b> * Transaction Price
            </Typography>
            <Typography variant="subtitle1">
              <b>Quantity:</b> * Transaction Quantity
            </Typography>
            <Typography variant="subtitle1">
              <b>Description:</b> Transaction Description
            </Typography>
            <Typography variant="subtitle1">
              <b>DebitAccountCode:</b> * Transaction Debit account code
            </Typography>
            <Typography variant="subtitle1">
              <b>CreditAccountCode:</b> * Transaction Credit account code
            </Typography>
          </div>
        </div>
      </WideBox>
    </FlexDiv>
  );
};

export default CsvImport;
